import { useEffect, useState } from "react";
import { t, tx } from "../i18n";

function rgba(hex, alpha) {
  if (!hex) return `rgba(10,10,12,${alpha})`;
  if (hex.startsWith("rgb")) return hex;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export const CHAT_DEMO = {
  messages: [
    { id: "d1", author: "Ana Live", text: t("Que overlay lindo! 🔥"), isMember: true },
    { id: "d2", author: "Bruno TV", text: t("Salve da resenha, tmj!") },
    { id: "d3", author: "Carla Games", text: t("Manda um salve pro Recife 🙌"), isMod: true },
  ],
  pinned: { author: "Diego F.", text: t("Primeira vez aqui e já virei fã do canal!") },
  alert: { author: "Elis M.", amount: "R$ 50,00", text: t("Continue com esse trabalho incrível! ❤️"), tier: 4 },
};

function Badge({ m }) {
  if (m.isOwner) return <span style={{ fontSize: "0.55em", background: "#cc0000", color: "#fff", borderRadius: 4, padding: "1px 5px", marginLeft: 6, verticalAlign: "middle" }}>{t("DONO")}</span>;
  if (m.isMod) return <span style={{ fontSize: "0.55em", background: "#5b8def", color: "#fff", borderRadius: 4, padding: "1px 5px", marginLeft: 6, verticalAlign: "middle" }}>MOD</span>;
  if (m.isMember) return <span style={{ fontSize: "0.55em", background: "#2ba640", color: "#fff", borderRadius: 4, padding: "1px 5px", marginLeft: 6, verticalAlign: "middle" }}>{t("MEMBRO")}</span>;
  return null;
}

export function ChatFeed({ el, messages, preview }) {
  const s = el.estilo || {};
  const src = (messages && messages.length ? messages : (preview ? CHAT_DEMO.messages : []));
  const list = src.slice(-(el.maxMensagens || 8));
  return (
    <div data-testid="chat-feed" style={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end",
      gap: (s.fontSize || 26) * 0.4, background: rgba(s.bgColor, (s.bgOpacity ?? 68) / 100),
      borderRadius: s.borderRadius || 14, padding: s.padding || 16, overflow: "hidden",
      fontFamily: `'${s.fontFamily || "Manrope"}', sans-serif`,
    }}>
      {list.map((m) => (
        <div key={m.id} style={{ fontSize: `${s.fontSize || 26}px`, lineHeight: 1.28, color: s.color || "#fff", display: "flex", alignItems: "flex-start", gap: 8 }}>
          {el.mostrarAvatar && m.avatar && (
            <img src={m.avatar} alt="" style={{ width: (s.fontSize || 26) * 1.1, height: (s.fontSize || 26) * 1.1, borderRadius: "50%", flexShrink: 0, marginTop: 2 }} />
          )}
          <div>
            <span style={{ color: m.isSuper ? "#ffd54a" : (s.authorColor || "#7fb0ff"), fontWeight: 800 }}>
              {m.source && (
                <span style={{
                  display: "inline-block", width: (s.fontSize || 26) * 0.3, height: (s.fontSize || 26) * 0.3,
                  borderRadius: "50%", background: m.source === "twitch" ? "#9146FF" : "#ff0000",
                  marginRight: 6, verticalAlign: "middle",
                }} />
              )}
              {m.author}
            </span>
            <Badge m={m} />
            {m.isSuper && m.amount && <span style={{ color: "#ffd54a", fontWeight: 800, marginLeft: 8 }}>{tx(m.amount)}</span>}
            <span style={{ marginLeft: 8 }}>{m.text}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PinnedComment({ el, pinned, preview }) {
  const p = pinned || (preview ? CHAT_DEMO.pinned : null);
  if (!p) return null;
  const s = el.estilo || {};
  return (
    <div data-testid="pinned-comment" style={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center",
      gap: (s.fontSize || 40) * 0.2, background: rgba(s.bgColor, (s.bgOpacity ?? 90) / 100),
      borderRadius: s.borderRadius || 18, padding: s.padding || 26, overflow: "hidden",
      fontFamily: `'${s.fontFamily || "Manrope"}', sans-serif`, boxSizing: "border-box",
    }}>
      <div style={{ color: s.authorColor || "#7fb0ff", fontWeight: 800, fontSize: `${(s.fontSize || 40) * 0.62}px` }}>{p.author}</div>
      <div style={{ color: s.color || "#fff", fontWeight: 700, fontSize: `${s.fontSize || 40}px`, lineHeight: 1.2 }}>{p.text}</div>
    </div>
  );
}

export function SuperChatAlert({ el, alert, preview }) {
  const [visible, setVisible] = useState(!!preview);
  useEffect(() => {
    if (preview) { setVisible(true); return; }
    if (alert && alert.key) {
      setVisible(true);
      const t = setTimeout(() => setVisible(false), (el.duracao || 8) * 1000);
      return () => clearTimeout(t);
    }
  }, [alert?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const a = alert || (preview ? CHAT_DEMO.alert : null);
  if (!visible || !a) return null;
  const s = el.estilo || {};
  return (
    <div data-testid="superchat-alert" style={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center",
      gap: (s.fontSize || 34) * 0.18, background: rgba(s.bgColor || "#1a73e8", (s.bgOpacity ?? 100) / 100),
      borderRadius: s.borderRadius || 18, padding: s.padding || 22, overflow: "hidden",
      fontFamily: `'${s.fontFamily || "Manrope"}', sans-serif`, boxSizing: "border-box",
      animation: "scaleIn 400ms ease both", boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color: "#fff", fontWeight: 800, fontSize: `${(s.fontSize || 34) * 0.78}px` }}>💬 {a.author}</span>
        <span style={{ color: "#fff", fontWeight: 900, fontSize: `${s.fontSize || 34}px`, background: "rgba(0,0,0,0.22)", padding: "2px 14px", borderRadius: 999 }}>{tx(a.amount)}</span>
      </div>
      {a.text && <div style={{ color: "#fff", fontSize: `${(s.fontSize || 34) * 0.72}px`, lineHeight: 1.25 }}>{a.text}</div>}
    </div>
  );
}
